// features/auth/pages/LoginPage.tsx
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Alert,
  TextField,
  Typography,
  styled,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from "@mui/material";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from "../../../contexts/AuthContext";
import { useInstitution } from "../../../contexts/InstitutionContext";
import { KullaniciTuru } from "../../../types/enums";
import { StyledBox } from "../../../components/common/StyledBox";
import { ErrorMessage } from "../../../components/common/ErrorMessage";
import { StyledButton } from "../../../components/common/StyledButton";
import { LoginRequestModal } from "../../../types/RequestModals/LoginRequestModal";
import { login } from "../../../services/api/authService";
import { LoginError } from "../../../types/ErrorModals/LoginError";
import { formatTimeRemaining } from "../../../utils/formatTimeRemaining";

const ModernRadio = styled(Radio)(({ theme }) => ({
  '&.Mui-checked': {
    color: '#FF8C00',
  },
  padding: '12px',
}));

const AnimatedPaper = styled(Paper)(({ theme }) => ({
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: '#FF8C00',
      borderWidth: '2px',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#FF8C00',
    },
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: '#FF8C00',
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#FF8C00',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  height: '48px',
  background: '#FF8C00',
  color: '#FFFFFF',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: '#FF7000',
    color: '#FFFFFF',
    transform: 'scale(1.02)',
    boxShadow: '0 3px 5px rgba(255, 140, 0, 0.3)',
  },
  '&.Mui-disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(255, 255, 255, 0.7)',
  },
}));

const initialCredentials: LoginRequestModal = {
  kullaniciTuru: KullaniciTuru.Ogrenci,
  kurumKodu: "",
  kullaniciAdi: "",
  sifre: "",
};

const demoOgrenciCredentials: LoginRequestModal = {
  kullaniciTuru: KullaniciTuru.Ogrenci,
  kurumKodu: "hal1555",
  kullaniciAdi: "11111111",
  sifre: "2222",
};

const demoOgretmenCredentials: LoginRequestModal = {
  kullaniciTuru: KullaniciTuru.Ogretmen,
  kurumKodu: "hal1555",
  kullaniciAdi: "50",
  sifre: "5555",
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setKullaniciModal } = useAuth();
  const { institution, selectedSubInstitution, setSelectedSubInstitution, hasSubInstitutions } = useInstitution();
  const [error, setError] = useState<string>("");
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState<"ogrenci" | "ogretmen" | "idare">("ogrenci");

  // Kurum kodunu otomatik olarak ayarla
  const [credentials, setCredentials] = useState<LoginRequestModal>(() => {
    // Alt kurum seçiliyse onun kodunu, değilse ana kurum kodunu kullan
    const kurumKodu = selectedSubInstitution ? selectedSubInstitution.code : institution.code || "";
    return {
      ...initialCredentials,
      kurumKodu
    };
  });

  const [formErrors, setFormErrors] = useState({
    kurumKodu: '',
    kullaniciAdi: '',
    sifre: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Credentials'ı seçilen alt kuruma göre güncelle
  useEffect(() => {
    if (selectedSubInstitution) {
      setCredentials(prev => ({
        ...prev,
        kurumKodu: selectedSubInstitution.code
      }));
    } else if (institution.code) {
      setCredentials(prev => ({
        ...prev,
        kurumKodu: institution.code
      }));
    }
  }, [selectedSubInstitution, institution]);

  // Alt kurum değişikliğini yönet
  const handleSubInstitutionChange = (event: SelectChangeEvent) => {
    const subId = event.target.value;
    const subInst = institution.subInstitutions?.find(s => s.id === subId) || null;
    setSelectedSubInstitution(subInst);
  };

  // Kurum kodu alanını gösterme/gizleme mantığı
  const shouldShowInstitutionCodeField = !institution.code && !selectedSubInstitution;

  const validateForm = () => {
    let isValid = true;
    const errors = {
      kurumKodu: '',
      kullaniciAdi: '',
      sifre: ''
    };

    // Eğer kurum kodu alanı gösteriliyorsa kontrol et
    if (shouldShowInstitutionCodeField && !credentials.kurumKodu.trim()) {
      errors.kurumKodu = 'Kurum kodu zorunludur';
      isValid = false;
    }

    if (!credentials.kullaniciAdi.trim()) {
      errors.kullaniciAdi = 'Kullanıcı adı zorunludur';
      isValid = false;
    }

    if (!credentials.sifre.trim()) {
      errors.sifre = 'Şifre zorunludur';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  }, []);

  const handleLoginTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginType(event.target.value as "ogrenci" | "ogretmen" | "idare");
    setError("");
    setFormErrors({
      kurumKodu: '',
      kullaniciAdi: '',
      sifre: ''
    });
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Lütfen tüm alanları doldurun',
        severity: 'error'
      });
      return;
    }

    setError("");
    setLoading(true);

    try {
      const kullaniciTuru = loginType === "ogrenci"
        ? KullaniciTuru.Ogrenci
        : loginType === "ogretmen" ?
          KullaniciTuru.Ogretmen
          : KullaniciTuru.Idare;

      // Kurum kodunu seçilen alt kurumdan veya ana kurumdan al
      const kurumKodu = selectedSubInstitution
        ? selectedSubInstitution.code
        : institution.code || credentials.kurumKodu;

      const loginCredentials = {
        ...credentials,
        kullaniciTuru,
        kurumKodu
      };

      console.log("Login credentials:", loginCredentials); // Debug için

      const kullaniciModal = await login(loginCredentials);
      setKullaniciModal(kullaniciModal);

      // Başarılı giriş bilgilerini kaydet
      localStorage.setItem("kurumKodu", loginCredentials.kurumKodu);

      // Seçilen alt kurumu da kaydet
      if (selectedSubInstitution) {
        localStorage.setItem(`${institution.id}_selectedSubInstitution`, selectedSubInstitution.id);
      }

      setSnackbar({
        open: true,
        message: 'Giriş başarılı!',
        severity: 'success'
      });

      const locationState = location.state as { from: { pathname: string } };
      const from = locationState?.from?.pathname || "/mainScreen";
      navigate(from, { replace: true });
    } catch (err) {
      const loginError = err as LoginError;
      if (loginError.remainingAttempts !== undefined) {
        setRemainingAttempts(loginError.remainingAttempts);
        setError(loginError.message);
      } else if (loginError.blockedUntil) {
        setIsBlocked(true);
        setBlockedUntil(new Date(loginError.blockedUntil));
        setError(loginError.message);
      } else {
        setError(loginError.message || "Giriş başarısız. Lütfen bilgilerinizi kontrol edin.");
      }
      setSnackbar({
        open: true,
        message: loginError.message || "Giriş başarısız",
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = useCallback(async (demoCredentials: LoginRequestModal) => {
    setError("");
    setLoading(true);
    setFormErrors({
      kurumKodu: '',
      kullaniciAdi: '',
      sifre: ''
    });

    try {
      // Kurum kodu seçilen alt kurumdan veya ana kurumdan al
      const kurumKodu = selectedSubInstitution
        ? selectedSubInstitution.code
        : institution.code || demoCredentials.kurumKodu;

      const updatedDemoCredentials = {
        ...demoCredentials,
        kurumKodu
      };

      setCredentials(updatedDemoCredentials);
      setLoginType(updatedDemoCredentials.kullaniciTuru === KullaniciTuru.Ogrenci ? 'ogrenci' : 'ogretmen');

      setSnackbar({
        open: true,
        message: 'Demo hesap ile giriş yapılıyor...',
        severity: 'info'
      });

      const kullaniciModal = await login(updatedDemoCredentials);
      setKullaniciModal(kullaniciModal);

      // Başarılı giriş bilgilerini kaydet
      localStorage.setItem("kurumKodu", updatedDemoCredentials.kurumKodu);

      // Seçilen alt kurumu da kaydet
      if (selectedSubInstitution) {
        localStorage.setItem(`${institution.id}_selectedSubInstitution`, selectedSubInstitution.id);
      }

      setSnackbar({
        open: true,
        message: 'Demo giriş başarılı! Yönlendiriliyorsunuz...',
        severity: 'success'
      });

      setTimeout(() => {
        const locationState = location.state as { from: { pathname: string } };
        const from = locationState?.from?.pathname || "/mainScreen";
        navigate(from, { replace: true });
      }, 1000);
    } catch (err) {
      const loginError = err as LoginError;
      if (loginError.remainingAttempts !== undefined) {
        setRemainingAttempts(loginError.remainingAttempts);
        setError(loginError.message);
        setSnackbar({
          open: true,
          message: `Kalan deneme hakkı: ${loginError.remainingAttempts}`,
          severity: 'warning'
        });
      } else if (loginError.blockedUntil) {
        setIsBlocked(true);
        setBlockedUntil(new Date(loginError.blockedUntil));
        setError(loginError.message);
        setSnackbar({
          open: true,
          message: 'Hesap geçici olarak bloke edildi',
          severity: 'error'
        });
      } else {
        setError(loginError.message || "Demo giriş başarısız. Lütfen daha sonra tekrar deneyin.");
        setSnackbar({
          open: true,
          message: loginError.message || "Demo giriş başarısız",
          severity: 'error'
        });
      }
      setCredentials(initialCredentials);
    } finally {
      setLoading(false);
    }
  }, [navigate, location, setKullaniciModal, institution, selectedSubInstitution]);

  // Form alanlarını güncelle
  const formFields = useMemo(() => {
    const fields = [
      // Kurum kodu alanını sadece gerektiğinde göster
      ...(shouldShowInstitutionCodeField ? [{
        id: "kurumKodu",
        label: "Kurum Kodu",
        name: "kurumKodu",
        value: credentials.kurumKodu,
        icon: <SchoolIcon />,
        error: !!formErrors.kurumKodu,
        helperText: formErrors.kurumKodu,
      }] : []),
      {
        id: "kullaniciAdi",
        label: "Kullanıcı Adı",
        name: "kullaniciAdi",
        value: credentials.kullaniciAdi,
        icon: <PersonIcon />,
        error: !!formErrors.kullaniciAdi,
        helperText: formErrors.kullaniciAdi,
      },
      {
        id: "sifre",
        label: "Şifre",
        name: "sifre",
        value: credentials.sifre,
        type: "password",
        icon: <LockIcon />,
        error: !!formErrors.sifre,
        helperText: formErrors.sifre,
      },
    ];
    return fields;
  }, [credentials, formErrors, shouldShowInstitutionCodeField]);

  // Alt kurum seçim bileşeni
  const SubInstitutionSelector = () => {
    if (!hasSubInstitutions) return null;

    return (
      <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
        <InputLabel id="sub-institution-label"
          sx={{
            '&.Mui-focused': {
              color: institution.primaryColor
            }
          }}
        >
          Okul/Kurum Seçiniz
        </InputLabel>
        <Select
          labelId="sub-institution-label"
          id="sub-institution-select"
          value={selectedSubInstitution?.id || ''}
          label="Okul/Kurum Seçiniz"
          onChange={handleSubInstitutionChange}
          sx={{
            '&.MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: institution.primaryColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: institution.primaryColor,
              },
            },
          }}
        >
          {institution.subInstitutions?.map((subInst) => (
            <MenuItem key={subInst.id} value={subInst.id}>
              {subInst.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  // Button stillerini kuruma göre dinamik olarak ayarla
  const DynamicAnimatedButton = useMemo(() => styled(Button)(({ theme }) => ({
    height: '48px',
    background: institution.primaryColor,
    color: '#FFFFFF',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: institution.secondaryColor || institution.primaryColor,
      color: '#FFFFFF',
      transform: 'scale(1.02)',
      boxShadow: `0 3px 5px rgba(0, 0, 0, 0.3)`,
    },
    '&.Mui-disabled': {
      background: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(255, 255, 255, 0.7)',
    },
  })), [institution]);

  // Radio button stillerini kuruma göre dinamik olarak ayarla
  const DynamicModernRadio = useMemo(() => styled(Radio)(({ theme }) => ({
    '&.Mui-checked': {
      color: institution.primaryColor,
    },
    padding: '12px',
  })), [institution]);

  return (
    <StyledBox
      sx={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)',
      }}
    >
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={true} timeout={800}>
          <AnimatedPaper
            elevation={3}
            sx={{
              padding: { xs: 2, sm: 4 },
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box
                sx={{
                  mb: 3,
                  transform: 'scale(1)',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <img
                  src={institution.logo}
                  alt={`${institution.name} Logo`}
                  style={{
                    width: "250px",
                    height: "auto",
                    maxHeight: "130",
                    filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))'
                  }}
                  loading="lazy"
                  onError={(e) => {
                    // Logo yüklenemezse varsayılan logoyu göster
                    const target = e.target as HTMLImageElement;
                    target.src = '/renkli_logo.png';
                  }}
                />
              </Box>

              {/* Kurum adını göster */}
              {/* <Typography variant="h6" sx={{ mb: 2, color: institution.primaryColor }}>
                {institution.name}
              </Typography> */}

              {/* Alt kurum seçici */}
              <SubInstitutionSelector />

              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  row
                  value={loginType}
                  onChange={handleLoginTypeChange}
                  sx={{
                    justifyContent: 'center',
                    mb: 2,
                    '& .MuiFormControlLabel-root': {
                      margin: '0 16px',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      }
                    }
                  }}
                >
                  <FormControlLabel
                    value="ogrenci"
                    control={<DynamicModernRadio />}
                    label="Öğrenci"
                  />
                  <FormControlLabel
                    value="ogretmen"
                    control={<DynamicModernRadio />}
                    label="Öğretmen"
                  />
                  {/* <FormControlLabel
                    value="idare"
                    control={<DynamicModernRadio />}
                    label="İdare"
                    sx={{
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      }
                    }}
                  /> */}
                </RadioGroup>
              </FormControl>

              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{ width: '100%' }}
              >
                {formFields.map((field) => (
                  <ModernTextField
                    key={field.id}
                    margin="normal"
                    required
                    fullWidth
                    id={field.id}
                    label={field.label}
                    name={field.name}
                    type={field.type || "text"}
                    value={field.value}
                    onChange={handleChange}
                    error={field.error}
                    helperText={field.helperText}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, color: institution.primaryColor }}>
                          {field.icon}
                        </Box>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: institution.primaryColor,
                          borderWidth: '2px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: institution.primaryColor,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        '&.Mui-focused': {
                          color: institution.primaryColor,
                        },
                      },
                    }}
                  />
                ))}

                {error && (
                  <ErrorMessage>
                    <Box mt={1} fontWeight="regular">
                      {error}
                    </Box>
                    {isBlocked && blockedUntil && (
                      <Box mt={1} fontWeight="bold" color="white">
                        Tekrar deneyebilmek için beklemeniz gereken süre:{" "}
                        {formatTimeRemaining(blockedUntil)}
                      </Box>
                    )}
                  </ErrorMessage>
                )}

                <DynamicAnimatedButton
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2, mb: 2 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "GİRİŞ YAP"
                  )}
                </DynamicAnimatedButton>

                {/* Demo butonlarını sadece default kurumda göster */}
                {!selectedSubInstitution && shouldShowInstitutionCodeField && (
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 2
                  }}>
                    <DynamicAnimatedButton
                      fullWidth
                      onClick={() => handleDemoLogin(demoOgrenciCredentials)}
                      disabled={loading}
                    >
                      DEMO ÖĞRENCİ
                    </DynamicAnimatedButton>
                    <DynamicAnimatedButton
                      fullWidth
                      onClick={() => handleDemoLogin(demoOgretmenCredentials)}
                      disabled={loading}
                    >
                      DEMO ÖĞRETMEN
                    </DynamicAnimatedButton>
                  </Box>
                )}
              </Box>
            </Box>
          </AnimatedPaper>
        </Fade>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
            '& .MuiAlert-icon': {
              fontSize: '24px'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
};

// Memoize the component for performance optimization
export default React.memo(LoginPage);